import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import VideoSourceUpload from '../components/videoToGif/VideoSourceUpload'
import VideoTrimFilmstrip from '../components/videoToGif/VideoTrimFilmstrip'
import VideoGifSettings from '../components/videoToGif/VideoGifSettings'
import VideoDownloadPanel from '../components/videoToGif/VideoDownloadPanel'
import type {LoadedVideo} from '../components/videoToGif/loadConverterVideoFile'
import Seo from '../components/seo/Seo'
import {SITE_URL} from '../components/seo/seoConstants'
import {CtaSection} from '../components/seo/seoSections'
import {trackEvent, trackEventDebounced} from '../../common/analytics/trackEvent'
import {estimateGifSizeRange} from '../../common/utils/estimateGifSize'
import {
  DEFAULT_GIF_COLORS,
  DEFAULT_OUTPUT_FPS,
  DEFAULT_OUTPUT_LONG_EDGE_PX,
  MAX_TRIM_DURATION_SEC,
} from '../../common/constants/videoToGifLimits'
import '../components/seo/SeoPage.css'
import '../components/converterShared/ConverterShared.css'
import './VideoToGifPage.css'

const JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Ecranify Video to GIF Converter',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Web browser',
  url: `${SITE_URL}/video-to-gif`,
  description: 'Free video-to-GIF converter — trim a clip and export an optimized, shareable GIF.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
}

const VideoToGifPage = () => {
  const {t} = useTranslation()

  const [video, setVideo] = useState<LoadedVideo | null>(null)
  const [trimStart, setTrimStart] = useState(0)
  const [trimEnd, setTrimEnd] = useState(MAX_TRIM_DURATION_SEC)
  const [longEdgePx, setLongEdgePx] = useState(DEFAULT_OUTPUT_LONG_EDGE_PX)
  const [fps, setFps] = useState(DEFAULT_OUTPUT_FPS)
  const [colors, setColors] = useState(DEFAULT_GIF_COLORS)
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    trackEvent('video_to_gif_viewed')
  }, [])

  // Revoke the previous object URL whenever it's superseded, and on unmount.
  useEffect(() => {
    objectUrlRef.current = video?.objectUrl ?? null
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [video])

  const handleVideoLoaded = useCallback((loaded: LoadedVideo) => {
    setVideo(loaded)
    setTrimStart(0)
    setTrimEnd(Math.min(loaded.duration, MAX_TRIM_DURATION_SEC))
  }, [])

  const handleClear = useCallback(() => {
    setVideo(null)
    setTrimStart(0)
    setTrimEnd(MAX_TRIM_DURATION_SEC)
  }, [])

  const handleTrimChange = useCallback((start: number, end: number) => {
    setTrimStart(start)
    setTrimEnd(end)
    trackEventDebounced('video_to_gif_trim', 'video_to_gif_trim_changed', {duration_sec: Math.round(end - start)})
  }, [])

  // Matches the server's `scale={longEdge}:-1` filter (fe/be config docs) — width
  // is pinned to longEdgePx, height derived to preserve the source aspect ratio.
  const estimatedSizeRange = useMemo(() => {
    if (!video) return null
    const outputHeightPx = Math.round(longEdgePx * (video.height / video.width))
    return estimateGifSizeRange({
      outputWidthPx: longEdgePx,
      outputHeightPx,
      fps,
      durationSec: trimEnd - trimStart,
      colors,
    })
  }, [video, longEdgePx, fps, colors, trimStart, trimEnd])

  return (
    <div className="vtg-root seo-root">
      <Seo
        title="Free Video to GIF Converter — Trim & Export | Ecranify"
        description="Trim a video clip and convert it to an optimized GIF for free. Upload, pick a start/end point and resolution, download — no signup required."
        canonicalPath="/video-to-gif"
        jsonLd={[JSON_LD]}
      />
      <div className="vtg-grid" aria-hidden />

      <main className="vtg-page">
        <header className="vtg-hero">
          <span className="vtg-eyebrow">
            <span className="vtg-eyebrow-dot" />
            {t('videoToGif.hero.eyebrow')}
          </span>
          <h1 className="vtg-title">
            {t('videoToGif.hero.titlePrefix')} <em>{t('videoToGif.hero.titleEm')}</em>
          </h1>
          <p className="vtg-lede">{t('videoToGif.hero.subtitle')}</p>
        </header>

        <div className="vtg-layout">
          <section className="vtg-col vtg-col-source">
            <h2 className="vtg-section-title">{t('videoToGif.sections.source')}</h2>
            <VideoSourceUpload video={video} onVideoLoaded={handleVideoLoaded} onClear={handleClear} />
          </section>

          <section className="vtg-col vtg-col-preview">
            <h2 className="vtg-section-title">{t('videoToGif.sections.trim')}</h2>
            <VideoTrimFilmstrip
              video={video}
              trimStart={trimStart}
              trimEnd={trimEnd}
              onChange={handleTrimChange}
              onVideoLoaded={handleVideoLoaded}
            />
          </section>

          <section className="vtg-col vtg-col-export">
            <h2 className="vtg-section-title">{t('videoToGif.sections.settings')}</h2>
            <VideoGifSettings
              longEdgePx={longEdgePx}
              fps={fps}
              colors={colors}
              trimmedDurationSec={trimEnd - trimStart}
              estimatedSizeRange={estimatedSizeRange}
              onLongEdgeChange={setLongEdgePx}
              onFpsChange={setFps}
              onColorsChange={setColors}
            />
            <h2 className="vtg-section-title">{t('videoToGif.sections.download')}</h2>
            <VideoDownloadPanel
              video={video}
              trimStart={trimStart}
              trimEnd={trimEnd}
              longEdgePx={longEdgePx}
              fps={fps}
              colors={colors}
            />
          </section>
        </div>

        <section className="vtg-seo">
          <h2 className="vtg-seo-title">{t('videoToGif.seo.title')}</h2>
          <p className="vtg-seo-body">{t('videoToGif.seo.body1')}</p>
          <p className="vtg-seo-body">{t('videoToGif.seo.body2')}</p>
        </section>

        <CtaSection cta={{title: t('videoToGif.cta.title'), body: t('videoToGif.cta.body')}} />
      </main>
    </div>
  )
}

export default VideoToGifPage
